// Tiny, dependency-free Markdown → HTML renderer.
//
// Deliberately self-contained (no `marked`) so the article reader keeps working
// in the offline fallback and never adds a build-time resolution risk. It covers
// exactly the subset our grammar articles use: ATX headings, GFM tables,
// unordered/ordered lists, blockquotes, horizontal rules, paragraphs, and the
// inline run of **bold**, *italic*, `code`. Everything is HTML-escaped first, so
// article content can never inject markup.
import { escapeHtml } from "./helpers.js";

// ---- Inline formatting (applied to already-escaped text) ----------------

function inline(text) {
  let s = text;
  // `code` first so its contents aren't re-processed for bold/italic.
  s = s.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-sage-50 text-sage-700 text-[0.85em]">$1</code>');
  // Image: ![alt](url) — before link processing (which also matches "](url)").
  // The src is constrained to http(s) or a root-relative path (PocketBase files
  // are served from /api/files/...), so no javascript: URL can slip through.
  s = s.replace(
    /!\[([^\]]*)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/g,
    '<img src="$2" alt="$1" loading="lazy" class="my-4 rounded-lg border border-sage-100 max-w-full h-auto" />'
  );
  // Internal article link: [label](article:slug). The slug is constrained to a
  // safe character class so no markup or javascript: URL can slip through (the
  // whole input was HTML-escaped up front). article.js wires the click to the
  // SPA router via the data attribute; the href is a working hash fallback.
  s = s.replace(
    /\[([^\]]+)\]\(article:([a-z0-9-]+)\)/g,
    (_m, label, slug) =>
      `<a href="#/article?slug=${slug}" data-article-link="${slug}" class="text-sage-700 font-medium underline decoration-sage-300 underline-offset-2 hover:decoration-sage-500">${label}</a>`
  );
  // Internal app-view link: [label](view:route). Lets a retired article point to
  // a live app page (e.g. the consolidated Sentence Structure view). The href is
  // a working hash-router fallback; article.js also wires the click via the data
  // attribute. Route is constrained to a safe character class.
  s = s.replace(
    /\[([^\]]+)\]\(view:([a-z0-9-]+)\)/g,
    (_m, label, view) =>
      `<a href="#/${view}" data-view-link="${view}" class="text-sage-700 font-medium underline decoration-sage-300 underline-offset-2 hover:decoration-sage-500">${label}</a>`
  );
  // External link: [label](https://…). Only http(s) is accepted (no javascript:
  // URLs), and it opens in a new tab with noopener for safety in shared notes.
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-sage-700 font-medium underline decoration-sage-300 underline-offset-2 hover:decoration-sage-500">$1</a>'
  );
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-sage-800">$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  return s;
}

// GitHub-style callouts. A blockquote whose first line is `[!TYPE]` (optionally
// followed by a custom title) becomes a coloured admonition box. Class strings
// are written out in full so Tailwind's content scanner keeps them.
const CALLOUTS = {
  TIP: { icon: "🌱", label: "Tip", box: "border-emerald-200 bg-emerald-50/70", title: "text-emerald-800", body: "text-emerald-900/80" },
  NOTE: { icon: "📝", label: "Note", box: "border-sky-200 bg-sky-50/70", title: "text-sky-800", body: "text-sky-900/80" },
  WARNING: { icon: "⚠️", label: "Watch out", box: "border-amber-200 bg-amber-50/70", title: "text-amber-800", body: "text-amber-900/80" },
  PITFALL: { icon: "🚩", label: "Common trap", box: "border-rose-200 bg-rose-50/70", title: "text-rose-800", body: "text-rose-900/80" },
  KEY: { icon: "🔑", label: "Key idea", box: "border-sage-300 bg-sage-50/70", title: "text-sage-800", body: "text-sage-700" },
  EXAMPLE: { icon: "💬", label: "Example", box: "border-violet-200 bg-violet-50/70", title: "text-violet-800", body: "text-violet-900/80" },
};

// Render the lines inside a callout, supporting paragraphs and simple bullets.
function renderCalloutBody(bodyLines) {
  const pieces = [];
  let para = [];
  let bullets = [];
  const flushPara = () => {
    if (para.length) pieces.push(`<p>${inline(para.join(" "))}</p>`);
    para = [];
  };
  const flushBullets = () => {
    if (bullets.length)
      pieces.push(`<ul class="list-disc pl-5 space-y-1">${bullets.map((b) => `<li>${inline(b)}</li>`).join("")}</ul>`);
    bullets = [];
  };
  for (const ln of bodyLines) {
    if (!ln.trim()) {
      flushPara();
      flushBullets();
      continue;
    }
    const bm = ln.match(/^\s*[-*]\s+(.*)$/);
    if (bm) {
      flushPara();
      bullets.push(bm[1]);
      continue;
    }
    flushBullets();
    para.push(ln.trim());
  }
  flushPara();
  flushBullets();
  return pieces.join("");
}

function splitRow(line) {
  // Split a GFM table row on unescaped pipes, dropping the leading/trailing one.
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
}

const isTableSep = (line) => /^\s*\|?[\s:]*-{2,}[-\s:|]*\|?\s*$/.test(line) && line.includes("-");

// ---- Block parser -------------------------------------------------------

export function renderMarkdown(md) {
  const lines = escapeHtml(md || "").replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let i = 0;

  const flushParagraph = (buf) => {
    if (buf.length) out.push(`<p class="text-sage-700 leading-relaxed my-3">${inline(buf.join(" "))}</p>`);
    buf.length = 0;
  };

  const paraBuf = [];

  while (i < lines.length) {
    const line = lines[i];

    // Blank line → paragraph break.
    if (!line.trim()) {
      flushParagraph(paraBuf);
      i++;
      continue;
    }

    // Horizontal rule.
    if (/^\s*---\s*$/.test(line)) {
      flushParagraph(paraBuf);
      out.push('<hr class="my-6 border-sage-100" />');
      i++;
      continue;
    }

    // Figure token: a line that is only `[[figure:some-id]]` becomes an empty
    // slot. figures.js injects the trusted, code-authored SVG after render (see
    // hydrateFigures). Brackets survive escapeHtml untouched, so this matches.
    const fig = line.match(/^\s*\[\[figure:([a-z0-9-]+)\]\]\s*$/);
    if (fig) {
      flushParagraph(paraBuf);
      out.push(`<div class="article-figure my-6" data-figure="${fig[1]}"></div>`);
      i++;
      continue;
    }

    // CTA button: a line that is only `[[cta:route|Label]]` renders as a big
    // call-to-action linking to an app view. article.js wires the click via the
    // data-view-link attribute (the href is a working hash fallback).
    const cta = line.match(/^\s*\[\[cta:([a-z0-9-]+)\|([^\]]+)\]\]\s*$/);
    if (cta) {
      flushParagraph(paraBuf);
      out.push(
        `<div class="my-6"><a href="#/${cta[1]}" data-view-link="${cta[1]}" class="inline-flex items-center gap-2 bg-sage-500 hover:bg-sage-600 text-white px-6 py-3.5 rounded-xl font-semibold text-base shadow-sm transition no-underline">${cta[2]}</a></div>`
      );
      i++;
      continue;
    }

    // Heading.
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushParagraph(paraBuf);
      const level = h[1].length;
      const sizes = { 1: "text-2xl mt-2 mb-4", 2: "text-xl mt-7 mb-3", 3: "text-lg mt-5 mb-2" };
      const cls = sizes[level] || "text-base mt-4 mb-2";
      out.push(`<h${level} class="font-bold text-sage-800 ${cls}">${inline(h[2])}</h${level}>`);
      i++;
      continue;
    }

    // GFM table: header row followed by a separator row.
    if (line.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushParagraph(paraBuf);
      const header = splitRow(line);
      i += 2; // skip header + separator
      const bodyRows = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) {
        bodyRows.push(splitRow(lines[i]));
        i++;
      }
      const thead = `<thead><tr>${header
        .map((c) => `<th class="text-left font-semibold text-sage-600 text-xs uppercase tracking-wide px-3 py-2 border-b border-sage-200">${inline(c)}</th>`)
        .join("")}</tr></thead>`;
      const tbody = `<tbody>${bodyRows
        .map(
          (r) =>
            `<tr class="border-b border-sage-50 last:border-0">${r
              .map((c) => `<td class="px-3 py-2 align-top text-sage-700">${inline(c)}</td>`)
              .join("")}</tr>`
        )
        .join("")}</tbody>`;
      out.push(`<div class="overflow-x-auto my-4"><table class="w-full text-sm border border-sage-100 rounded-lg">${thead}${tbody}</table></div>`);
      continue;
    }

    // Blockquote (possibly multi-line). Note: the whole input is HTML-escaped
    // up front, so a leading ">" arrives here as "&gt;".
    if (/^\s*&gt;\s?/.test(line)) {
      flushParagraph(paraBuf);
      const quote = [];
      while (i < lines.length && /^\s*&gt;\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*&gt;\s?/, ""));
        i++;
      }

      // Callout? First line looks like "[!TIP]" or "[!WARNING] Custom title".
      const cm = quote[0] && quote[0].match(/^\[!(\w+)\]\s*(.*)$/);
      const callout = cm && CALLOUTS[cm[1].toUpperCase()];
      if (callout) {
        const title = (cm[2] || "").trim() || callout.label;
        const bodyHtml = renderCalloutBody(quote.slice(1));
        out.push(
          `<div class="callout ${callout.box} my-4 rounded-xl border p-4 flex gap-3">
            <span class="callout-icon text-xl leading-none select-none" aria-hidden="true">${callout.icon}</span>
            <div class="min-w-0 flex-1">
              <p class="callout-title font-semibold ${callout.title} mb-1">${inline(title)}</p>
              <div class="callout-body text-sm leading-relaxed ${callout.body} space-y-2">${bodyHtml}</div>
            </div>
          </div>`
        );
        continue;
      }

      out.push(`<blockquote class="border-l-4 border-sage-300 bg-sage-50/60 pl-4 pr-3 py-2 my-4 text-sage-700 italic rounded-r">${inline(quote.join(" "))}</blockquote>`);
      continue;
    }

    // Unordered list.
    if (/^\s*[-*]\s+/.test(line)) {
      flushParagraph(paraBuf);
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      out.push(`<ul class="list-disc pl-6 my-3 space-y-1 text-sage-700">${items.map((it) => `<li>${inline(it)}</li>`).join("")}</ul>`);
      continue;
    }

    // Ordered list.
    if (/^\s*\d+\.\s+/.test(line)) {
      flushParagraph(paraBuf);
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      out.push(`<ol class="list-decimal pl-6 my-3 space-y-1 text-sage-700">${items.map((it) => `<li>${inline(it)}</li>`).join("")}</ol>`);
      continue;
    }

    // Default: accumulate into the current paragraph.
    paraBuf.push(line.trim());
    i++;
  }

  flushParagraph(paraBuf);
  return out.join("\n");
}
